<template>
  <div class="mt-10">
    <div v-if="caterers.length > 0">
      <h1 class="mt-8 text-center text-2xl lg:text-3xl font-semibold">
        Caterers near by
      </h1>

      <div class="mt-4 max-w-3xl mx-auto">
        <div
          class="grid grid-cols-12 rounded bg-white mt-8 cursor-pointer shadow-lg p-2"
          v-for="caterer in caterers"
          :key="caterer.id"
        >
          <div class="img col-span-12 sm:col-span-4 relative h-40 sm:h-auto">
            <img
              v-if="caterer.logo"
              class="object-contain h-full w-full absolute inset-0"
              :src="caterer.logo"
              :alt="caterer.name"
            />
            <CatererLogoPlaceholder v-else :name="caterer.name" />
          </div>
          <div class="content col-span-12 sm:col-span-8 p-6">
            <div class="text-lg w-full">{{ caterer.name }}</div>
            <div class="text-xs mt-2">
              {{ caterer.description }}
            </div>
            <div
              class="flex flex-col sm:flex-row flex-wrap justify-between mt-2"
            >
              <div class="font-medium text-xs">
                <span class="mr-8 block md:inline"
                  >{{ caterer.city }}, {{ caterer.state }}</span
                >
                <span>{{ caterer.address }}</span>
              </div>

              <div class="font-medium text-xs">
                {{ caterer.phone }}
              </div>
            </div>

            <button
              class="btn btn-primary block font-semibold text-xs rounded px-10 w-full ml-auto mr-0 sm:w-auto sm:flex-none mt-5"
              @click.stop="loadMenu(caterer.id)"
            >
              Show Menu
            </button>
          </div>
        </div>
      </div>
    </div>

    <slot v-if="notFound" name="notFoundTemplate">
      <div class="p-3 sm:p-5">
        <h1 class="font-semibold mb-6 text-center text-2xl lg:text-3xl">
          Unfortunately there are no caterers in the BuildaBox.co directory at
          this time
        </h1>
        <p class="mb-6 text-center">
          There are currently no caterers in our directory for this postal code.
          Try different location.
        </p>
        <button
          class="btn btn-primary font-semibold rounded px-10 w-full sm:w-40 mx-auto block"
          @click="$emit('cancel')"
        >
          <span>Cancel</span>
        </button>
      </div>
    </slot>

    <Modal v-if="showMenuModal" size="lg" @closeOnOverlay="showMenuModal = false">
      <div slot="body">
        <div class="grid grid-cols-12 gap-24 mx-auto px-4">
          <!-- Left panel -->
          <div class="col-span-12 lg:col-span-3">
            <div class="mx-auto mb-6">
              <h1 class="font-semibold text-3xl mb-8">Categories</h1>

              <ul class="list-disc ml-5">
                <li
                  class="py-2 cursor-pointer hover:font-semibold"
                  v-for="(category, index) in categories"
                  :key="index"
                  v-scroll-to="{
                    el: '#category' + category.id,
                    container: '.modal-mask',
                    offset: -5
                  }"
                >
                  {{ category.name }}
                </li>
              </ul>
            </div>
          </div>

          <!-- Right panel -->
          <div class="col-span-12 lg:col-span-9">
            <h1
              class="font-semibold text-2xl lg:text-3xl text-center mb-8"
              v-if="pickedCaterer.name"
            >
              {{ pickedCaterer.name }} menu preview
            </h1>
            <section class="mt-5" style="min-height: 100px">
              <div v-if="loading">
                <Spinner class="mx-auto" orange />
              </div>

              <div v-else>
                <div
                  v-for="(category, index) in categories"
                  :key="index"
                  class="mb-2"
                >
                  <header
                    class="mb-8 flex items-center justify-between"
                    :id="'category' + category.id"
                  >
                    <div class="flex">
                      <div
                        class="w-20 h-20 rounded overflow-hidden flex items-center justify-center flex-shrink-0 mr-4 bg-gray-200 cursor-pointer hover:shadow-lg"
                      >
                        <LightBox :img="category.image">
                          <img
                            v-if="category.image"
                            :src="category.image"
                            :alt="category.name"
                          />
                        </LightBox>
                      </div>
                      <div>
                        <h1
                          class="text-xl lg:text-2xl text-primary font-extrabold text-left py-2"
                        >
                          {{ category.name }}
                        </h1>
                        <p class="text-sm opacity-75">
                          {{ category.description }}
                        </p>
                      </div>
                    </div>

                    <ArrowIcon
                      class="flex-shrink-0"
                      width="22"
                      height="14"
                      :direction="category.isExpanded ? 'top' : 'bottom'"
                      @click="category.isExpanded = !category.isExpanded"
                    />
                  </header>
                  <div
                    class="pl-20 max-h-0 overflow-hidden ease-in-out duration-700"
                    :class="[!category.isExpanded ? 'max-h-0' : 'max-h-screen']"
                  >
                    <div
                      v-for="item in category.items"
                      :key="item.id"
                      class="mb-6 last:mb-12"
                    >
                      <div
                        class="flex items-center justify-between md:items-start"
                      >
                        <div
                          class="w-20 h-20 rounded overflow-hidden flex items-center justify-center flex-shrink-0 bg-gray-100 cursor-pointer hover:shadow-lg"
                        >
                          <LightBox :img="item.image">
                            <img
                              v-if="item.image"
                              :src="item.image"
                              :alt="item.name"
                            />
                          </LightBox>
                        </div>
                        <div class="mx-5 flex-grow hidden md:block">
                          <div class="font-base font-semibold mb-1">
                            {{ item.name }}
                          </div>
                          <div class="text-sm opacity-75">
                            {{ item.description }}
                          </div>
                        </div>
                      </div>
                      <div class="md:hidden py-5">
                        <div class="font-base font-semibold mb-1">
                          {{ item.name }}
                        </div>
                        <div class="text-sm opacity-75">
                          {{ item.description }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            <section class="mt-20">
              <div class="flex flex-col-reverse sm:flex-row justify-between">
                <button
                  class="btn btn-secondary font-semibold rounded-lg px-10 min-w-10 mt-5 sm:mt-0"
                  @click="showMenuModal = false"
                >
                  <span>Cancel</span>
                </button>
                <button
                  class="btn btn-primary font-semibold rounded-lg px-10 min-w-10"
                  @click="submitCaterer"
                >
                  <Spinner v-if="loading" class="mx-auto" />
                  <span v-else>Select Business</span>
                </button>
              </div>
            </section>
          </div>
        </div>
      </div>
      <div slot="footer"></div>
    </Modal>
  </div>
</template>

<script>
import Spinner from "@/components/common/Spinner";
import Api from "@/api/api_v1.0";
import Modal from "@/components/common/Modal";
import CatererLogoPlaceholder from "@/components/caterer/CatererLogoPlaceholder";
import LightBox from "../common/LightBoxComponent.vue";
import ArrowIcon from "@/components/common/icons/ArrowIcon";

export default {
  name: "OrderSearchCaterers",

  components: {
    Spinner,
    Modal,
    CatererLogoPlaceholder,
    LightBox,
    ArrowIcon
  },

  props: ["zip"],

  data() {
    return {
      loading: false,
      caterers: [],
      categories: [],
      pickedCatererId: null,
      showMenuModal: false,
      notFound: false
    };
  },

  mounted() {
    if (this.zip && this.zip.length > 0) {
      this.submitLocation();
    }
  },

  watch: {
    zip: function(oldValue, newValue) {
      this.submitLocation();
    }
  },

  methods: {
    submitLocation() {
      this.loading = true;
      this.notFound = false;

      Api.getCaterersByLocation(this.zip)
        .then(res => {
          this.caterers = res.data;

          if (this.caterers.length === 0) {
            this.notFound = true;
            this.$emit("locationNotFound", this.zip);
          }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    loadMenu(caterer_id) {
      this.pickedCatererId = caterer_id;
      this.loading = true;
      Api.getCatererMenu(caterer_id)
        .then(response => {
          let categories = response.data.menu;
          categories = categories.map(function(category) {
            return {
              ...category,
              isExpanded: false
            };
          });
          this.categories = categories;
          this.showMenuModal = true;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    submitCaterer() {
      this.showMenuModal = false;
      this.$emit("select", { zip: this.zip, id: this.pickedCatererId });
    }
  },

  computed: {
    pickedCaterer() {
      return this.pickedCatererId
        ? this.caterers.find(caterer => caterer.id === this.pickedCatererId)
        : null;
    }
  },

  filters: {
    truncate: function(text, length, suffix) {
      if (text.length > length) {
        return text.substring(0, length) + suffix;
      } else {
        return text;
      }
    }
  }
};
</script>

<style scoped></style>
